import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import { EMAILJS_CONFIG } from '../config/emailjs';

const Contact = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        eventType: '',
        message: ''
    });

    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    // Input sanitization fonksiyonu
    const sanitizeInput = (input) => {
        if (typeof input !== 'string') return input;

        return input
            .replace(/[<>]/g, '') // HTML tag karakterlerini kaldır
            .replace(/javascript:/gi, '') // JavaScript protokolünü kaldır
            .replace(/on\w+=/gi, '') // Event handler'ları kaldır
            .trim(); // Başlangıç ve bitiş boşluklarını kaldır
    };

    // Email validasyonu
    const isValidEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    // Telefon validasyonu
    const isValidPhone = (phone) => {
        if (!phone) return true; // Telefon opsiyonel
        const phoneRegex = /^[\d\s\-\+\(\)]+$/;
        return phoneRegex.test(phone);
    };

    // Etkinlik tiplerini Türkçe'ye çeviren fonksiyon
    const getEventTypeInTurkish = (eventType) => {
        const eventTypes = {
            'wedding': 'Düğün',
            'engagement': 'Nişan',
            'birthday': 'Doğum Günü',
            'corporate': 'Kurumsal',
            'henna': 'Kına Gecesi',
            'circumcision': 'Sünnet',
            'other': 'Diğer'
        };
        return eventTypes[eventType] || eventType;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        const sanitizedValue = sanitizeInput(value);

        setFormData(prevState => ({
            ...prevState,
            [name]: sanitizedValue
        }));

        // Hata mesajını temizle
        if (error) setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        // Form validasyonu
        if (!formData.name || !formData.email || !formData.eventType) {
            setError('Lütfen zorunlu alanları doldurun.');
            setIsLoading(false);
            return;
        }

        if (!isValidEmail(formData.email)) {
            setError('Lütfen geçerli bir e-posta adresi girin.');
            setIsLoading(false);
            return;
        }

        if (!isValidPhone(formData.phone)) {
            setError('Lütfen geçerli bir telefon numarası girin.');
            setIsLoading(false);
            return;
        }

        // Tüm form verilerini sanitize et
        const sanitizedData = {
            name: sanitizeInput(formData.name),
            email: sanitizeInput(formData.email),
            phone: sanitizeInput(formData.phone),
            eventType: formData.eventType, // Select değeri zaten güvenli
            message: sanitizeInput(formData.message)
        };

        try {
            // EmailJS ile email gönderme
            const result = await emailjs.send(
                EMAILJS_CONFIG.SERVICE_ID,
                EMAILJS_CONFIG.TEMPLATE_ID,
                {
                    name: sanitizedData.name,
                    email: sanitizedData.email,
                    phone: sanitizedData.phone,
                    eventType: getEventTypeInTurkish(sanitizedData.eventType),
                    message: sanitizedData.message,
                    to_email: EMAILJS_CONFIG.TO_EMAIL
                },
                EMAILJS_CONFIG.PUBLIC_KEY
            );

            console.log('Email başarıyla gönderildi:', result.text);
            setIsSubmitted(true);

            // Form başarıyla gönderildikten sonra 3 saniye içinde mesajı kaldır
            setTimeout(() => {
                setIsSubmitted(false);
                // Formu sıfırla
                setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    eventType: '',
                    message: ''
                });
            }, 3000);

        } catch (error) {
            console.error('Email gönderme hatası:', error);
            setError('Mesaj gönderilirken bir hata oluştu. Lütfen tekrar deneyin.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <section id="iletisim" className="relative py-16 lg:py-20 bg-gradient-to-b from-gray-50 to-white overflow-hidden">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
                {/* Başlık bölümü */}
                <div className="text-center mb-16 animate-fade-in">
                    <div className="inline-block bg-gradient-to-r from-primary-100 to-primary-200 text-primary-800 px-6 py-3 rounded-full text-sm font-semibold mb-6 shadow-lg">
                        📞 İletişime Geçin
                    </div>
                    <h2 className="text-4xl lg:text-5xl font-bold text-dark-900 mb-4 animate-slide-up">
                        Hayalinizdeki Daveti
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-primary-800 block mt-1 pt-2 pb-2 leading-relaxed">
                            Birlikte Tasarlayalım
                        </span>
                    </h2>
                    <p className="text-lg text-dark-600 max-w-3xl mx-auto leading-relaxed animate-slide-up" style={{ animationDelay: '0.2s' }}>
                        Özel gününüz için dijital davetiye oluşturmak mı istiyorsunuz? Formu doldurun, uzman ekibimiz
                        en kısa sürede sizinle iletişime geçsin.
                    </p>
                </div>

                {/* İletişim container */}
                <div className="grid md:grid-cols-2 gap-8 mb-12">

                    {/* Sol kolon - İletişim Formu */}
                    <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 animate-slide-up" style={{ animationDelay: '0.3s' }}>
                        <h3 className="text-xl font-bold text-dark-800 mb-4">Bize Ulaşın</h3>

                        {isSubmitted && (
                            <div className="bg-green-50 border border-green-200 text-green-700 px-3 py-2 rounded-lg mb-4 animate-fade-in">
                                <div className="flex items-center">
                                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    <span className="text-sm font-medium">Mesajınız başarıyla gönderildi!</span>
                                </div>
                            </div>
                        )}

                        {error && (
                            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg mb-4 animate-fade-in">
                                <div className="flex items-center">
                                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                    <span className="text-sm font-medium">{error}</span>
                                </div>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-3">
                            {/* Ad Soyad */}
                            <div>
                                <label htmlFor="name" className="block text-sm font-medium text-dark-700 mb-1">
                                    Ad Soyad <span className="text-primary-600">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300"
                                    placeholder="Adınız ve soyadınız"
                                    required
                                />
                            </div>

                            {/* Email */}
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-dark-700 mb-1">
                                    E-posta <span className="text-primary-600">*</span>
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300"
                                    placeholder="ornek@mail.com"
                                    required
                                />
                            </div>

                            {/* Telefon */}
                            <div>
                                <label htmlFor="phone" className="block text-sm font-medium text-dark-700 mb-1">
                                    Telefon
                                </label>
                                <input
                                    type="tel"
                                    id="phone"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300"
                                    placeholder="0555 123 4567"
                                />
                            </div>

                            {/* Etkinlik Tipi */}
                            <div>
                                <label htmlFor="eventType" className="block text-sm font-medium text-dark-700 mb-1">
                                    Etkinlik Tipi <span className="text-primary-600">*</span>
                                </label>
                                <select
                                    id="eventType"
                                    name="eventType"
                                    value={formData.eventType}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300"
                                    required
                                >
                                    <option value="" disabled>Etkinlik tipini seçiniz</option>
                                    <option value="wedding">Düğün</option>
                                    <option value="engagement">Nişan</option>
                                    <option value="birthday">Doğum Günü</option>
                                    <option value="corporate">Kurumsal</option>
                                    <option value="henna">Kına Gecesi</option>
                                    <option value="circumcision">Sünnet</option>
                                    <option value="other">Diğer</option>
                                </select>
                            </div>

                            {/* Mesaj */}
                            <div>
                                <label htmlFor="message" className="block text-sm font-medium text-dark-700 mb-1">
                                    Mesajınız
                                </label>
                                <textarea
                                    id="message"
                                    name="message"
                                    value={formData.message}
                                    onChange={handleChange}
                                    rows="3"
                                    className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300"
                                    placeholder="Etkinliğiniz hakkında bize bilgi verin..."
                                ></textarea>
                            </div>

                            {/* Gönder butonu */}
                            <div>
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="w-full bg-gradient-to-r from-primary-600 to-primary-700 text-white font-semibold py-3 px-4 rounded-lg hover:from-primary-700 hover:to-primary-800 transition-all duration-300 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isLoading ? (
                                        <div className="flex items-center justify-center">
                                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Gönderiliyor...
                                        </div>
                                    ) : (
                                        'Mesajı Gönder'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>

                    {/* Sağ kolon - İletişim Bilgileri ve Harita */}
                    <div className="flex flex-col space-y-4 animate-slide-up" style={{ animationDelay: '0.5s' }}>

                        {/* İletişim Bilgileri */}
                        <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100">
                            <h3 className="text-xl font-bold text-dark-800 mb-4">İletişim Bilgileri</h3>

                            <div className="space-y-3">
                                {/* Adres */}
                                <div className="flex items-start space-x-3">

                                </div>

                                {/* Email */}
                                <div className="flex items-start space-x-3">
                                    <div className="flex-shrink-0 bg-primary-100 p-2 rounded-lg">
                                        <svg className="w-4 h-4 text-primary-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-dark-800">E-posta</h4>
                                        <p className="text-sm text-dark-600">dijivite@outlook.com</p>
                                    </div>
                                </div>

                                {/* Telefon */}
                                <div className="flex items-start space-x-3">
                                    <div className="flex-shrink-0 bg-primary-100 p-2 rounded-lg">
                                        <svg className="w-4 h-4 text-primary-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-dark-800">Telefon</h4>
                                        <p className="text-sm text-dark-600">+90 (544) 464 34 87</p>
                                    </div>
                                </div>


                            </div>

                            {/* Sosyal Medya */}
                            <div className="mt-8">
                                <h4 className="text-lg font-semibold text-dark-800 mb-4">Bizi Takip Edin</h4>
                                <div className="flex space-x-4">

                                    <a href="https://www.instagram.com/dijivite/?utm_source=ig_web_button_share_sheet" target="_blank" rel="noopener noreferrer" className="bg-primary-100 hover:bg-primary-200 text-primary-700 p-3 rounded-full transition-all duration-300 transform hover:-translate-y-1">
                                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                                        </svg>
                                    </a>


                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Alt bilgilendirme bölümü */}
                <div className="text-center mt-12 animate-slide-up" style={{ animationDelay: '0.6s' }}>
                    <div className="bg-gradient-to-r from-primary-50 to-primary-100 rounded-3xl p-8 lg:p-12 shadow-lg text-primary-900 relative overflow-hidden border border-primary-200">
                        {/* Arka plan pattern */}
                        <div className="absolute inset-0 opacity-10">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-primary-500 rounded-full -translate-y-8 translate-x-8"></div>
                            <div className="absolute bottom-0 left-0 w-24 h-24 bg-primary-500 rounded-full translate-y-6 -translate-x-6"></div>
                        </div>

                        <div className="relative">
                            <h3 className="text-2xl lg:text-3xl font-bold mb-4">
                                Hemen Başlayalım!
                            </h3>
                            <p className="text-primary-700 mb-8 max-w-2xl mx-auto">
                                Dijital davetiyenizi hazırlamak için ilk adımı atın. Size özel fiyat tekliflerimizi hemen alın.
                            </p>
                            <a href="tel:05444643487" className="inline-block bg-gradient-to-r from-primary-600 to-primary-700 text-white px-8 py-4 rounded-xl hover:from-primary-700 hover:to-primary-800 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1">
                                Hemen Arayın
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Contact;